/**
 * WorkOrderPartsPanel
 * -------------------
 * Pulls the parts side of each contractor quote onto the work order, lets the
 * desk flag what is on backorder, and opens a linked Sourcing Desk request for
 * those lines. The flag clears automatically when the parts are delivered.
 *
 * GET  /api/admin/work-orders/{id}/parts
 * PUT  /api/admin/work-orders/{id}/parts
 * POST /api/admin/work-orders/{id}/parts/sourcing-request
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, PackageSearch, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) => (v || v === 0
  ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : "—");

export default function WorkOrderPartsPanel({ workOrderId, token, onChanged }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/work-orders/${workOrderId}/parts`, { headers });
      setData(r.data);
      setRows(r.data?.parts_lines || []);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the parts list");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId, token]);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, patch) => {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const removeRow = (i) => { setRows((rs) => rs.filter((_, idx) => idx !== i)); setDirty(true); };
  const addRow = (seed = {}) => {
    setRows((rs) => [...rs, {
      code: "", description: "", quantity: 1, uom: "", unit_price: "", need_by: "",
      backordered: false, received: false, ...seed,
    }]);
    setDirty(true);
  };

  const pullFromQuote = (q) => {
    if (q.lines?.length) {
      q.lines.forEach((l) => addRow({
        code: "", description: l.description || l.code, quantity: l.quantity || 1,
        unit_price: l.unit_price, quote_id: q.quote_id, contractor_name: q.contractor_name,
      }));
      toast.success(`${q.lines.length} line(s) pulled from ${q.contractor_name}`);
      return;
    }
    addRow({
      code: "",
      description: q.work_description || `Parts quoted by ${q.contractor_name}`,
      quantity: 1,
      unit_price: q.parts_total ?? "",
      quote_id: q.quote_id,
      contractor_name: q.contractor_name,
    });
    toast.success(`Parts line added from ${q.contractor_name} — add the part number and qty`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/work-orders/${workOrderId}/parts`,
        { parts_lines: rows }, { headers });
      setRows(r.data?.parts_lines || rows);
      setDirty(false);
      toast.success(r.data?.parts_on_backorder
        ? "Saved — this work order is now flagged PARTS ON BACKORDER"
        : "Parts list saved");
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the parts list");
    } finally {
      setSaving(false);
    }
  };

  const openSourcing = async () => {
    if (dirty) return toast.error("Save the parts list first.");
    setOpening(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/work-orders/${workOrderId}/parts/sourcing-request`, {}, { headers });
      toast.success(r.data?.created
        ? `Sourcing request ${r.data.backorder_id} opened`
        : `Already linked to ${r.data.backorder_id}`, {
        description: "Quote it from the Sourcing Desk — this job stays flagged until delivery.",
      });
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open the sourcing request");
    } finally {
      setOpening(false);
    }
  };

  const link = data?.sourcing_request;
  const flagged = data?.parts_on_backorder;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4" data-testid="wo-parts-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <PackageSearch className="w-4 h-4 text-slate-600" /> Parts &amp; Backorders
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Pull the parts side of a contractor quote, flag anything on backorder, then open a
            linked Sourcing Desk request to source it. The flag clears on delivery.
          </p>
        </div>
        {flagged && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-rose-100 text-rose-800 border border-rose-300"
            data-testid="wo-parts-flag-badge">
            <AlertTriangle className="w-3 h-3" /> Parts on backorder
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Contractor quotes — parts vs labor */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Contractor quotes — parts vs labor
            </p>
            {(data?.quotes || []).length === 0 ? (
              <p className="text-xs text-slate-500">No contractor quotes on this work order yet.</p>
            ) : (
              <div className="space-y-2">
                {data.quotes.map((q) => (
                  <div key={q.quote_id}
                    className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2"
                    data-testid={`wo-parts-quote-${q.quote_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        {q.contractor_name}
                        {q.awarded && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                            Awarded
                          </span>
                        )}
                        {q.rejected && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                            Not selected
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Parts <strong className="text-slate-700">{money(q.parts_total)}</strong>
                        {" · "}Labor <strong className="text-slate-700">{money(q.labor_total)}</strong>
                        {" · "}Total {money(q.total_estimate)}
                        {q.lead_time ? ` · lead time ${q.lead_time}` : ""}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => pullFromQuote(q)}
                      data-testid={`wo-parts-pull-${q.quote_id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Pull parts
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Parts lines */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Parts needed for this job
              </p>
              <Button size="sm" variant="outline" onClick={() => addRow()}
                data-testid="wo-parts-add-line">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add part
              </Button>
            </div>
            {rows.length === 0 ? (
              <p className="text-xs text-slate-500">
                Nothing yet — pull the parts off a quote above, or add a part manually.
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1.1fr_2fr_0.6fr_1fr_0.9fr_1fr_28px] gap-1 bg-slate-100 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <span>Part #</span><span>Description</span><span>Qty</span><span>Need by</span>
                  <span>Unit price</span><span>Backorder</span><span />
                </div>
                {rows.map((r, i) => (
                  <div key={r.id || i}
                    className={`grid grid-cols-[1.1fr_2fr_0.6fr_1fr_0.9fr_1fr_28px] gap-1 px-2 py-1.5 border-t border-slate-100 ${
                      r.backordered && !r.received ? "bg-rose-50/60" : ""}`}
                    data-testid={`wo-parts-line-${i}`}>
                    <Input className="h-8 text-xs" value={r.code || ""}
                      onChange={(e) => setRow(i, { code: e.target.value })}
                      data-testid={`wo-parts-code-${i}`} />
                    <Input className="h-8 text-xs" value={r.description || ""}
                      onChange={(e) => setRow(i, { description: e.target.value })}
                      data-testid={`wo-parts-desc-${i}`} />
                    <Input className="h-8 text-xs" value={r.quantity ?? ""}
                      onChange={(e) => setRow(i, { quantity: e.target.value })}
                      data-testid={`wo-parts-qty-${i}`} />
                    <Input className="h-8 text-xs" value={r.need_by || ""}
                      placeholder="Oct 21" onChange={(e) => setRow(i, { need_by: e.target.value })}
                      data-testid={`wo-parts-needby-${i}`} />
                    <Input className="h-8 text-xs" value={r.unit_price ?? ""}
                      onChange={(e) => setRow(i, { unit_price: e.target.value })}
                      data-testid={`wo-parts-price-${i}`} />
                    <button type="button"
                      onClick={() => setRow(i, { backordered: !r.backordered })}
                      className={`h-8 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                        r.received
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : r.backordered
                            ? "bg-rose-600 text-white border-rose-600"
                            : "bg-white text-slate-500 border-slate-300 hover:border-slate-500"}`}
                      title={r.received ? "Delivered" : "Toggle on-backorder"}
                      data-testid={`wo-parts-backorder-${i}`}>
                      {r.received ? "Received" : r.backordered ? "On backorder" : "In stock"}
                    </button>
                    <button type="button" onClick={() => removeRow(i)}
                      className="text-slate-400 hover:text-rose-600"
                      data-testid={`wo-parts-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked sourcing request */}
          {link && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex flex-wrap items-center justify-between gap-2"
              data-testid="wo-parts-sourcing-link">
              <div>
                <p className="text-xs font-bold text-rose-900 uppercase tracking-wider">
                  Linked sourcing request
                </p>
                <p className="text-xs text-rose-800 mt-0.5">
                  <strong>{link.id}</strong> · status {link.status}
                  {link.awarded_supplier_name ? ` · awarded to ${link.awarded_supplier_name}` : ""}
                  {link.tracking_number ? ` · ${link.courier || ""} ${link.tracking_number}` : ""}
                  {link.delivered_at ? " · delivered" : ""}
                </p>
              </div>
              <a href={`/admin/backorders?open=${link.id}`} target="_blank" rel="noreferrer"
                className="text-xs font-bold text-rose-800 underline hover:text-rose-900 inline-flex items-center gap-1"
                data-testid="wo-parts-open-sourcing">
                Open in Sourcing Desk <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving || !dirty}
              className="bg-slate-900 hover:bg-slate-800" data-testid="wo-parts-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Save className="w-4 h-4 mr-1.5" />}
              {dirty ? "Save parts list" : "Saved"}
            </Button>
            {!link && (
              <Button variant="outline" onClick={openSourcing} disabled={opening}
                className="border-rose-300 text-rose-800 hover:bg-rose-50"
                data-testid="wo-parts-open-sourcing-btn">
                {opening ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <PackageSearch className="w-4 h-4 mr-1.5" />}
                Open backordered parts in Sourcing Desk
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
